// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-launch/FrameworkIntelligence.jsx

import React from 'react';
import MissionCard from './shared/MissionCard';
import MissionMetric from './shared/MissionMetric';
import './FrameworkIntelligence.css';

export default function FrameworkIntelligence({
  intelligence = {},
  onSubmit,
  saving = false,
}) {
  const applied = intelligence.applied_frameworks || [];
  const appliedKeys = new Set(
    applied
      .map((framework) => String(framework?.key || framework?.label || '').replace(/[\s_\-/]/g, '').toUpperCase())
      .filter(Boolean)
  );
  const opportunities = (intelligence.future_framework_opportunities || []).filter((framework) => {
    const identity = String(framework?.key || framework?.label || '').replace(/[\s_\-/]/g, '').toUpperCase();
    return !identity || !appliedKeys.has(identity);
  });
  const capacity = intelligence.framework_capacity || {};
  const hasAppliedFrameworks = applied.length > 0;
  const capacityConfigured = capacity.configured === true;
  const canContinue = hasAppliedFrameworks && capacityConfigured;

  let capacityText = 'Framework capacity not active';
  if (capacityConfigured && capacity.unlimited) {
    capacityText = `${applied.length} applied · Unlimited capacity`;
  } else if (capacityConfigured && Number.isInteger(capacity.limit)) {
    capacityText = `${applied.length} of ${capacity.limit} framework slots applied`;
  }

  const renderMetrics = (framework) => (
    <div className="framework-metrics">
      <MissionMetric
        label="Controls"
        value={framework.estimated_controls || '—'}
        compact
      />
      <MissionMetric
        label="Effort"
        value={framework.estimated_effort || 'Moderate'}
        compact
      />
    </div>
  );

  return (
    <div className="mission-step-shell">
      <MissionCard
        eyebrow="Phase 2 · Framework Intelligence"
        title="Your governed compliance program"
        description="Mission Launch shows the frameworks currently authorized for your organization."
      >
        <div className="framework-authority-summary" role="status">
          <span className="mission-authority-badge">Framework Authority</span>
          <strong>{capacityText}</strong>
        </div>

        {hasAppliedFrameworks ? (
          <div className="framework-section framework-section-applied">
            <div className="framework-section-heading framework-section-heading-applied">
              <div>
                <h4>Applied frameworks</h4>
                <p>Already authorized for this organization.</p>
              </div>
            </div>

            <div className="framework-grid framework-grid-applied">
              {applied.map((framework) => {
                const frameworkKey = String(framework.key || '').trim();
                const frameworkLabel = String(
                  framework.label || frameworkKey || 'Framework'
                ).trim();

                return (
                  <article
                    className="framework-intelligence-card framework-intelligence-card-selected framework-intelligence-card-applied"
                    key={frameworkKey || frameworkLabel}
                  >
                    <div>
                      <div className="framework-title-row">
                        <h4>{frameworkLabel}</h4>
                        <span className="framework-applied-badge">Applied</span>
                      </div>
                      <p>{framework.why || framework.description}</p>
                    </div>

                    <div className="framework-applied-footer">
                      <span>Governed by Framework Authority</span>
                      {renderMetrics(framework)}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="mission-launch-guardrail" role="status">
            <strong>No applied framework is available yet.</strong>
            <p>
              Framework Authority must apply at least one framework before Mission Launch
              can continue. Mission Launch cannot create or change framework authority.
            </p>
          </div>
        )}

        {!capacityConfigured ? (
          <div className="mission-launch-guardrail" role="status">
            <strong>Framework capacity is not active yet.</strong>
            <p>
              An active entitlement must provide framework.standard.max before Mission
              Launch can confirm the governed compliance program.
            </p>
          </div>
        ) : null}

        {opportunities.length ? (
          <div className="framework-section framework-section-opportunities">
            <h4>Future framework opportunities</h4>
            <p className="framework-section-copy">
              OASYS can identify frameworks that may become relevant as your compliance
              program expands. These are informational only and are not applied by Mission Launch.
            </p>
            <div className="framework-grid framework-grid-opportunities">
              {opportunities.map((framework) => {
                const frameworkKey = String(framework.key || '').trim();
                const frameworkLabel = String(
                  framework.label || frameworkKey || 'Framework'
                ).trim();

                return (
                  <article
                    className="framework-intelligence-card framework-intelligence-card-opportunity"
                    key={frameworkKey || frameworkLabel}
                  >
                    <div>
                      <div className="framework-title-row">
                        <h4>{frameworkLabel}</h4>
                        <span>Future opportunity</span>
                      </div>
                      <p>{framework.why || framework.description}</p>
                    </div>

                    {renderMetrics(framework)}
                  </article>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mission-step-actions">
          <button
            type="button"
            className="mission-primary-button"
            onClick={onSubmit}
            disabled={!canContinue || saving}
          >
            {saving ? 'Opening connectors…' : 'Continue to Connectors'}
          </button>
        </div>
      </MissionCard>
    </div>
  );
}
